/**
 * FERP Portal - Universal View Tracker
 *
 * Tracks which entity IDs the current user has viewed via localStorage.
 * "New" / "unviewed" = an entity the user has never opened/clicked on.
 *
 * Generalized from useDocumentViewTracker — works for any primitive type.
 *
 * Usage:
 *   const tracker = useViewTracker('workflow');
 *   tracker.markViewed(entity.id);
 *   const isNew = !tracker.isViewed(entity.id);
 *   const newCount = data.filter(d => !tracker.isViewed(d.id)).length;
 *
 * localStorage key: ferp:views:{scope}:{userId}
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '@/auth';

const STORAGE_PREFIX = 'ferp:views';

interface ViewTrackerData {
  viewedIds: string[];
  version: 1;
}

export interface UseViewTrackerReturn {
  /** Mark an entity as viewed */
  markViewed: (entityId: string) => void;
  /** Check if an entity has been viewed */
  isViewed: (entityId: string) => boolean;
  /** Set of all viewed entity IDs */
  viewedIds: Set<string>;
}

function buildKey(scope: string, userId: string): string {
  return `${STORAGE_PREFIX}:${scope}:${userId}`;
}

function readViewedIds(scope: string, userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(buildKey(scope, userId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as ViewTrackerData;
    if (parsed.version !== 1) return new Set();
    return new Set(parsed.viewedIds);
  } catch {
    return new Set();
  }
}

function writeViewedIds(scope: string, userId: string, ids: Set<string>): void {
  try {
    const data: ViewTrackerData = {
      viewedIds: Array.from(ids),
      version: 1,
    };
    localStorage.setItem(buildKey(scope, userId), JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

/**
 * Tracks which entities the current user has viewed.
 *
 * @param scope - Storage namespace (e.g. 'workflow', 'document', 'task')
 */
export function useViewTracker(scope: string): UseViewTrackerReturn {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const viewedIds = useMemo<Set<string>>(() => {
    if (!userId) return new Set();
    return readViewedIds(scope, userId);
  }, [scope, userId]);

  const markViewed = useCallback(
    (entityId: string): void => {
      if (!userId) return;
      const current = readViewedIds(scope, userId);
      if (current.has(entityId)) return;
      current.add(entityId);
      writeViewedIds(scope, userId, current);
    },
    [scope, userId],
  );

  const isViewed = useCallback(
    (entityId: string): boolean => {
      if (!userId) return true; // If no user, treat all as viewed (no "new" badges)
      return readViewedIds(scope, userId).has(entityId);
    },
    [scope, userId],
  );

  return { markViewed, isViewed, viewedIds };
}
